// FormApplication.tsx
import React, { useState, useRef, useEffect } from 'react';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import { Button, Grid, Snackbar, CircularProgress, Alert, AlertTitle, Typography } from '@mui/material';
import { Stepper, Step, StepButton, useMediaQuery, useTheme } from '@mui/material';
import IntroductionStep from './IntroductionStep';
import ProducersStep from './Producers/ProducersStep';
import BasicInfoStep from './BasicInfoStep';
import ReinsuranceContactStep from './ReinsuranceContactStep';
import ShareholdersStep from './PeopleStep';
import SupportingDocumentsStep from './SupportingDocumentsStep';
import DocuSignStep from './DocuSignStep';
import PaymentStep from './PaymentStep';
import { SignupData, FormStatus, createDefaultSignupData, OfficerType } from 'form-application-shared';
import { formApi } from '../services/api';

// Define step validation function type
type ValidationFunction = () => boolean;

// Initialize default form data
const initialFormData: SignupData = createDefaultSignupData();

function FormApplication() {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Use refs to store validation functions instead of state to prevent render loops
  const validateIntroductionStepRef = useRef<ValidationFunction | null>(null);
  const validateBasicInfoStepRef = useRef<ValidationFunction | null>(null);
  const validateProducersStepRef = useRef<ValidationFunction | null>(null);
  const validateReinsuranceContactStepRef = useRef<ValidationFunction | null>(null);
  const validateShareholdersStepRef = useRef<ValidationFunction | null>(null);
  const validateSupportingDocsStepRef = useRef<ValidationFunction | null>(null);
  const validatePaymentStepRef = useRef<ValidationFunction | null>(null);
  const validateDocuSignStepRef = useRef<ValidationFunction | null>(null);
  
  // State for loading and error handling
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<{saving: boolean, message: string | null}>({
    saving: false,
    message: null
  });
  
  // Define status tracking state
  const [formStatus, setFormStatus] = useState<FormStatus>(FormStatus.IN_PROGRESS);
  
  // Add state for DocuSign URL
  const [docusignUrl, setDocusignUrl] = useState<string | null>(null);
  
  // Track if user has completed DocuSign process
  const [docusignCompleted, setDocusignCompleted] = useState<boolean>(false);
  
  // State for snackbar notifications
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });
  
  const [formData, setFormData] = useState<SignupData>(initialFormData);
  const [formId, setFormId] = useState<string | null>(null);

  // Extract form ID from URL path and check for return from DocuSign
  useEffect(() => {
    const getFormIdFromUrl = () => {
      // Get the last segment of the pathname
      const pathParts = window.location.pathname.split('/');
      return pathParts[pathParts.length - 1];
    };
    
    const id = getFormIdFromUrl();
    
    if (id) {
      // Store form_id in local storage
      localStorage.setItem('form_id', id);

      setFormId(id);
    
      // Check if user is returning from DocuSign
      const urlParams = new URLSearchParams(window.location.search);
      const event = urlParams.get('event');
      
      // Update docusignCompleted based on URL parameter
      if (event === 'signing_complete') {
        setDocusignCompleted(true);
      }
    } else {
      setError("No form ID found in URL. Please check the link and try again.");
      setLoading(false);
    }
  }, []);

  // Load form data when ID is available
  useEffect(() => {
    if (!formId) return;
    
    const loadFormData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await formApi.getForm(formId);
        
        // Check if the form is already completed
        if (response.status === FormStatus.COMPLETED) {
          setFormData(response.data as SignupData);
          setLoading(false);
          // Store completion status
          setFormStatus(response.status);
          
          // Check if there's a docusignUrl in the response
          if (response.documentSigningUrl) {
            setDocusignUrl(response.documentSigningUrl);
          }
          
          return;
        }
        
        // If not completed, proceed as normal
        if (response && response.data) {
          const data = { ...response.data } as SignupData;
          
          if (!data.signedByEmail){
            data.signedByEmail = response.email;
          }
          
          // Ensure producers have initialized owners array with at least one owner
          if (data.producers) {
            data.producers = data.producers.map(producer => {
              // Initialize owners array if null
              if (!producer.owners) {
                producer.owners = [];
              }
              
              // Ensure at least one owner exists
              if (producer.owners.length === 0) {
                producer.owners.push({
                  legalName: "",
                  email: "",
                  taxId: "",
                  addressLines: "",
                  city: "",
                  state: "",
                  zip: "",
                  percentOwned: 0,
                  familyRelation: ""
                });
              }
              
              return producer;
            });
          }
          
          // Ensure at least one officer/shareholder exists
          if (!data.people || data.people.length === 0) {
            data.people = [
              {
                legalName: "",
                email: "",
                taxId: "",
                addressLines: "",
                city: "",
                state: "",
                zip: "",
                percentOwned: null,
                dob: "",
                isShareholder: false,
                officerTypes: [OfficerType.PRESIDENT],
                isDirector: false,
                relationship: "",
                otherRelationship: ""
              },
              {
                legalName: "",
                email: "",
                taxId: "",
                addressLines: "",
                city: "",
                state: "",
                zip: "",
                percentOwned: null,
                dob: "",
                isShareholder: false,
                officerTypes: [OfficerType.SECRETARY],
                isDirector: false,
                relationship: "",
                otherRelationship: ""
              }
            ];
          }
          
          setFormData(data);
        }
        
        setLoading(false);
      } catch (err) {
        console.error("Failed to load form data:", err);
        setError("Failed to load your form data. Please try again later.");
        setLoading(false);
      }
    };
    
    loadFormData();
  }, [formId]);

  // Sample data for testing - preserves existing fees
  const createSampleData = (): SignupData => {
    return {
      signedByName: "John Test Smith",
      signedByEmail: "john.test@example.com",
      hasReinsurance: true,
      noFinanceCompanyAck: true,
      
      // Basic Reinsurance Info
      reinsuranceName1: "Alpha",
      reinsuranceName2: "Beta", 
      reinsuranceName3: "Gamma",
      products: [
        "Collateral Protection Insurance (CPI)",
        "GAP",
        "Service Contract (VSC)"
      ],
      
      // Producers
      producers: [
        {
          legalName: "ABC Finance Company LLC",
          relationship: "Dealership",
          otherRelationship: "",
          contact: "Sarah Johnson",
          role: "Account Manager",
          email: "sarah.johnson@abcfinance.com",
          addressLines: "123 Finance Street\nSuite 100",
          city: "Columbus",
          state: "OH",
          zip: "43215",
          telephone: "(614) 555-0123",
          fax: "(614) 555-0124",
          taxId: "12-3456789",
          taxType: "Partnership",
          owners: [
            {
              legalName: "Michael Roberts",
              email: "michael.roberts@abcfinance.com",
              taxId: "123-45-6789",
              addressLines: "456 Oak Avenue",
              city: "Columbus",
              state: "OH",
              zip: "43220",
              percentOwned: 60,
              familyRelation: "None"
            },
            {
              legalName: "Jennifer Davis",
              email: "jennifer.davis@abcfinance.com",
              taxId: "987-65-4321",
              addressLines: "789 Pine Road",
              city: "Dublin",
              state: "OH",
              zip: "43017",
              percentOwned: 40,
              familyRelation: "None"
            }
          ]
        }
      ],
      
      // Reinsurance Contact
      reinsuranceContactName: "David Wilson",
      reinsuranceContactRelation: "President",
      reinsuranceContactEmail: "david.wilson@testcompany.com",
      reinsuranceContactPhone: "(614) 555-0200",
      reinsuranceContactAccountingEmail: "accounting@testcompany.com",
      reinsuranceContactPhysicalAddressLines: "1000 Corporate Blvd\nSuite 500",
      reinsuranceContactPhysicalCity: "Columbus",
      reinsuranceContactPhysicalState: "OH",
      reinsuranceContactPhysicalCounty: "Franklin",
      reinsuranceContactPhysicalZip: "43215",
      reinsuranceContactMailingEnabled: true,
      reinsuranceContactMailingAddressLines: "PO Box 12345",
      reinsuranceContactMailingCity: "Columbus",
      reinsuranceContactMailingState: "OH",
      reinsuranceContactMailingZip: "43216",
      shareholdersHaveOtherOwnership: true,
      otherOwnershipDetails: "ABC Insurance Holdings owns 25% of XYZ Reinsurance Company",
      reinsuranceFormationFee: formData.reinsuranceFormationFee, // Preserve existing fee
      reinsuranceManagementFee: formData.reinsuranceManagementFee, // Preserve existing fee
      
      // People (Directors, Officers, Shareholders)
      people: [
        {
          legalName: "David Wilson",
          email: "david.wilson@testcompany.com",
          taxId: "111-22-3333",
          addressLines: "2000 Executive Drive",
          city: "Columbus",
          state: "OH",
          zip: "43215",
          percentOwned: 45,
          dob: "1975-03-15",
          officerTypes: [OfficerType.PRESIDENT],
          isShareholder: true,
          isDirector: true,
          relationship: "Business Owner",
          otherRelationship: ""
        },
        {
          legalName: "Lisa Anderson",
          email: "lisa.anderson@testcompany.com",
          taxId: "444-55-6666",
          addressLines: "3000 Business Park Way",
          city: "Dublin",
          state: "OH",
          zip: "43017",
          percentOwned: 35,
          dob: "1980-07-22",
          officerTypes: [OfficerType.SECRETARY],
          isShareholder: true,
          isDirector: true,
          relationship: "Business Owner",
          otherRelationship: ""
        },
        {
          legalName: "Robert Thompson",
          email: "robert.thompson@testcompany.com",
          taxId: "777-88-9999",
          addressLines: "4000 Commerce Circle",
          city: "Westerville",
          state: "OH",
          zip: "43081",
          percentOwned: 20,
          dob: "1978-11-08",
          officerTypes: [OfficerType.TREASURER],
          isShareholder: true,
          isDirector: false,
          relationship: "Business Owner",
          otherRelationship: ""
        }
      ],
      
      // ACH Payment
      ach: {
        entityName: "Test Reinsurance Company LLC",
        bankName: "First National Bank of Ohio",
        routingNumber: "044000024",
        accountNumber: "1234567890",
        accountNumberConfirm: "1234567890",
        voidedCheckFile: null,
        voidedCheckFileName: "",
        voidedCheckFileType: "",
        voidedCheckFileId: null,
        optIn: true,
        acknowledgedTerms: true
      },
      
      // ID Uploads (empty - still need manual upload)
      idUploads: {}
    };
  };

  // Secret key combination handler (Ctrl+Shift+F9 or Cmd+Shift+F9)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Ctrl+Shift+F9 (Windows/Linux) or Cmd+Shift+F9 (Mac)
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'F9') {
        event.preventDefault();
        
        // Populate with sample data
        const sampleData = createSampleData();
        setFormData(sampleData);
        
        // Show success notification
        setSnackbar({
          open: true,
          message: "🎯 Sample data loaded! (Images still need manual upload)",
          severity: 'info'
        });
        
        // Auto-save the sample data will be handled by the existing auto-save logic
        console.log("Sample data will be auto-saved when navigating steps");
        
        console.log("Sample data populated for testing");
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyDown);
    
    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []); // No dependencies needed

  // Save form data when changing steps
  const saveFormData = async () => {
    if (!formId) return;
    
    try {
      setSaveStatus({
        saving: true,
        message: "Saving your progress..."
      });
      
      // The updateForm method expects a partial data object, not the whole formData
      await formApi.updateForm(formId, formData);
      
      // Use snackbar for success notification
      setSnackbar({
        open: true,
        message: "Progress saved successfully",
        severity: 'success'
      });
      
      setSaveStatus({
        saving: false,
        message: null
      });
      
    } catch (err) {
      console.error("Failed to save form data:", err);
      // Use snackbar for error notification
      setSnackbar({
        open: true,
        message: "Failed to save progress. We'll try again later.",
        severity: 'error'
      });
      
      setSaveStatus({
        saving: false,
        message: null
      });
    }
  };

  const handleValidationFunctionChange = (step: number, validationFn: ValidationFunction) => {
    if (formData.hasReinsurance) {
      // When hasReinsurance is true, the steps are in the reordered sequence
      switch(step) {
        case 0: // Introduction step
          validateIntroductionStepRef.current = validationFn;
          break;
        case 1: // Reinsurance Info step (BasicInfoStep)
          validateBasicInfoStepRef.current = validationFn;
          break;
        case 2: // Producers step
          validateProducersStepRef.current = validationFn;
          break;
        case 3: // Reinsurance Contact step
          validateReinsuranceContactStepRef.current = validationFn;
          break;
        case 4: // Officers & Shareholders step (combined)
          validateShareholdersStepRef.current = validationFn;
          break;
        case 5: // Supporting Documents step
          validateSupportingDocsStepRef.current = validationFn;
          break;
        case 6: // Payment step
          validatePaymentStepRef.current = validationFn;
          break;
        case 7: // DocuSign step
          validateDocuSignStepRef.current = validationFn;
          break;
        default:
          break;
      }
    } else {
      // When hasReinsurance is false, only the Introduction, Producers and DocuSign steps are shown
      switch(step) {
        case 0: // Introduction step
          validateIntroductionStepRef.current = validationFn;
          break;
        case 1: // Producers step
          validateProducersStepRef.current = validationFn;
          break;
        case 2: // DocuSign step
          validateDocuSignStepRef.current = validationFn;
          break;
        default:
          break;
      }
    }
  };

  // Define steps based on hasReinsurance
  const getSteps = () => {
    if (formData.hasReinsurance) {
      return [
        "Introduction",
        "Reinsurance Info",
        "Producers",
        "Contact Information",
        "Directors, Officers and Shareholders",
        "Documents",
        "ACH",
        "DocuSign"
      ];
    } else {
      return [
        "Introduction",
        "Producers",
        "DocuSign"
      ];
    }
  };

  const steps = getSteps();

  const renderStepContent = (step: number) => {
    if (!formData.hasReinsurance) {
      // Without reinsurance, show Introduction, Producers and DocuSign
      if (step === 0) {
        return (
          <IntroductionStep 
            formData={formData} 
            setFormData={setFormData} 
            onValidationChange={(validationFn) => handleValidationFunctionChange(0, validationFn)} 
          />
        );
      } else if (step === 1) {
        return (
          <ProducersStep 
            formData={formData} 
            setFormData={setFormData} 
            onValidationChange={(validationFn) => handleValidationFunctionChange(1, validationFn)} 
          />
        );
      } else if (step === 2) {
        return (
          <DocuSignStep 
            formData={formData} 
            setFormData={setFormData}
            onValidationChange={(validationFn) => handleValidationFunctionChange(2, validationFn)}
          />
        );
      }
    } else {
      // With reinsurance, show all steps
      switch (step) {
        case 0:
          return (
            <IntroductionStep 
              formData={formData} 
              setFormData={setFormData} 
              onValidationChange={(validationFn) => handleValidationFunctionChange(0, validationFn)}
            />
          );
        case 1:
          return (
            <BasicInfoStep 
              formData={formData} 
              setFormData={setFormData} 
              onValidationChange={(validationFn) => handleValidationFunctionChange(1, validationFn)}
            />
          );
        case 2:
          return (
            <ProducersStep 
              formData={formData} 
              setFormData={setFormData} 
              onValidationChange={(validationFn) => handleValidationFunctionChange(2, validationFn)} 
            />
          );
        case 3:
          return (
            <ReinsuranceContactStep 
              formData={formData} 
              setFormData={setFormData} 
              onValidationChange={(validationFn) => handleValidationFunctionChange(3, validationFn)}
            />
          );
        case 4:
          return (
            <ShareholdersStep 
              formData={formData} 
              setFormData={setFormData}
              onValidationChange={(validationFn) => handleValidationFunctionChange(4, validationFn)} 
            />
          );
        case 5:
          return (
            <SupportingDocumentsStep 
              formData={formData} 
              setFormData={setFormData}
              onValidationChange={(validationFn) => handleValidationFunctionChange(5, validationFn)}
              formId={formId}
            />
          );
        case 6:
          return (
            <PaymentStep 
              formData={formData} 
              setFormData={setFormData}
              onValidationChange={(validationFn) => handleValidationFunctionChange(6, validationFn)} 
              formId={formId}
            />
          );
        case 7:
          return (
            <DocuSignStep 
              formData={formData} 
              setFormData={setFormData}
              onValidationChange={(validationFn) => handleValidationFunctionChange(7, validationFn)}
            />
          );
      }
    }
    return null;
  };

  const handleNext = async () => {
    // Determine which validation function to use based on current step
    let validationFunction: ValidationFunction | null = null;
    
    if (formData.hasReinsurance) {
      switch(currentStep) {
        case 0: // Introduction step
          validationFunction = validateIntroductionStepRef.current;
          break;
        case 1: // Reinsurance Info (BasicInfoStep)
          validationFunction = validateBasicInfoStepRef.current;
          break;
        case 2: // Producers
          validationFunction = validateProducersStepRef.current;
          break;
        case 3: // Reinsurance Contact
          validationFunction = validateReinsuranceContactStepRef.current;
          break;
        case 4: // Officers & Shareholders (combined)
          validationFunction = validateShareholdersStepRef.current;
          break;
        case 5: // Supporting Documents
          validationFunction = validateSupportingDocsStepRef.current;
          break;
        case 6: // Payment
          validationFunction = validatePaymentStepRef.current;
          break;
        case 7: // DocuSign
          validationFunction = validateDocuSignStepRef.current;
          break;
        default:
          break;
      }
    }
    else {
      switch(currentStep) {
        case 0: // Introduction step
          validationFunction = validateIntroductionStepRef.current;
          break;
        case 1: // Producers step
          validationFunction = validateProducersStepRef.current;
          break;
        case 2: // DocuSign step
          validationFunction = validateDocuSignStepRef.current;
          break;
        default:
          break;
      }
    }
    
    // If there's a validation function for this step, run it
    if (validationFunction) {
      // If validation fails, don't proceed
      if (!validationFunction()) {
        return; // Exit the function early
      }
    }

    // Save form data before proceeding to next step
    if (formId) {
      try {
        await saveFormData();
      } catch (err) {
        console.error("Failed to save form data, but proceeding to next step:", err);
        // We'll still proceed to the next step even if save fails
      }
    }

    // Only proceed if validation passes or if there's no validation for this step
    setCurrentStep((prev) => prev + 1);
    
    // Scroll to top after changing step
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = async () => {
    // Save form data before going back
    if (formId) {
      try {
        await saveFormData();
      } catch (err) {
        console.error("Failed to save form data, but proceeding to previous step:", err);
        // We'll still go back even if save fails
      }
    }
    
    setCurrentStep((prev) => prev - 1);
    
    // Scroll to top after changing step
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleStepClick = async (index: number) => {
    // Only allow clicking to steps that have been completed or are the next step
    if (index <= currentStep + 1) {
      // Save form data before changing step
      if (formId) {
        try {
          await saveFormData();
        } catch (err) {
          console.error("Failed to save form data, but proceeding to selected step:", err);
          // We'll still change steps even if save fails
        }
      }
      
      setCurrentStep(index);
      
      // Scroll to top after changing step
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formId) {
      setError("Cannot submit form without a valid form ID.");
      return;
    }
    
    try {
      setSaveStatus({
        saving: true,
        message: "Submitting your application..."
      });
      
      // First save the current state
      await formApi.updateForm(formId, formData);
      
      try {
        // Then complete the signup
        const result = await formApi.completeSignup(formId);
        
        // Check if the result has a DocuSign URL
        if (result.documentSigningUrl) {
          setDocusignUrl(result.documentSigningUrl);
        }
        
        setSnackbar({
          open: true,
          message: "Your application has been submitted successfully!",
          severity: 'success'
        });
        
        // Update form status
        setFormStatus(FormStatus.COMPLETED);
        
      } catch (submitErr) {
        // Show alert for completion failure
        alert("There was an error completing your application. Your information has been saved, but please contact support at reinsurance@buckeyerisk.com to finalize your submission.");
        console.error("Failed to complete signup:", submitErr);
      }
      
      setSaveStatus({
        saving: false,
        message: null
      });
      
    } catch (err) {
      console.error("Failed to submit form:", err);
      setSnackbar({
        open: true,
        message: "Failed to submit application. Please try again.",
        severity: 'error'
      });
      
      setSaveStatus({
        saving: false,
        message: null
      });
    }
  };

  // Handler to navigate to DocuSign URL
  const handleSignNow = () => {
    if (docusignUrl) {
      window.location.href = docusignUrl;
    }
  };

  // Handler to duplicate the current form application
  const handleDuplicateApplication = async () => {
    if (!formId) return;
    
    try {
      setSaveStatus({
        saving: true,
        message: "Creating new application..."
      });
      
      const newFormId = await formApi.duplicateForm(formId);
      
      setSnackbar({
        open: true,
        message: "New application created successfully!",
        severity: 'success'
      });
      
      setSaveStatus({
        saving: false,
        message: null
      });
      
      // Navigate to the new form
      window.location.href = `/${newFormId}`;
      
    } catch (err) {
      console.error("Failed to duplicate form:", err);
      setSnackbar({
        open: true,
        message: "Failed to create new application. Please try again.",
        severity: 'error'
      });
      
      setSaveStatus({
        saving: false,
        message: null
      });
    }
  };

  // Show completed application message if status is COMPLETED or user completed DocuSign
  if (formStatus === FormStatus.COMPLETED || docusignCompleted) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Logo */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <img
            src={`/${import.meta.env.VITE_LOGO_PATH}`}
            alt="Logo"
            style={{ maxWidth: '400px', width: '100%', height: 'auto' }}
          />
        </Box>
        
        <Alert severity="success" sx={{ mb: 4 }}>
          <AlertTitle>Application Submitted</AlertTitle>
          {docusignCompleted 
            ? "All documents have been signed successfully! Your application is being processed."
            : "Your application has been successfully submitted and is awaiting signatures."}
        </Alert>
        
        <Box sx={{ mt: 4, p: 4, bgcolor: '#f5f5f5', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            {docusignCompleted ? "Signing Complete" : "Next Steps"}
          </Typography>
          
          {!docusignCompleted && (
            <>
            <Typography paragraph>
              You will receive your reinsurance application via email from DocuSign. Please review and sign. 
              Once executed, you will receive a finalized copy, and your application will be processed. 
              If you have any questions, please reach out to reinsurance@buckeyerisk.com or your Account Representative.
            </Typography>
              
              {/* Sign Now button if DocuSign URL is available */}
              {docusignUrl && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    size="large"
                    onClick={handleSignNow}
                  >
                    Sign Now
                  </Button>
                </Box>
              )}
            </>
          )}
          
          {/* Duplicate Application Message - Show regardless of DocuSign status */}
          <Box sx={{ mt: 4, p: 3, bgcolor: '#e3f2fd', borderRadius: 2, border: '1px solid #2196f3' }}>
            <Typography variant="h6" color="primary" gutterBottom>
              Have multiple reinsurance companies?
            </Typography>
            <Typography paragraph>
              Click the following button to start a new application process with the same information.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button 
                variant="contained" 
                color="secondary"
                onClick={handleDuplicateApplication}
                disabled={saveStatus.saving}
              >
                {saveStatus.saving ? "Creating..." : "Start New Application"}
              </Button>
            </Box>
          </Box>
          
          {docusignCompleted && (
            <>
              <Typography paragraph>
                You've successfully signed all documents. Your application is now being processed. 
                You will be contacted if any additional information is needed.
              </Typography>
              
              {/* Message to show when returning from DocuSign */}
              <Typography paragraph sx={{ mt: 2, fontWeight: 'bold', textAlign: 'center' }}>
                You can now close this window.
              </Typography>
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={() => window.close()}
                >
                  Close Window
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Container>
    );
  }

  // Show loading spinner while fetching data
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} thickness={4} />
          <Box sx={{ mt: 2 }}>Loading your application...</Box>
        </Box>
      </Container>
    );
  }

  // Show error message if loading failed
  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Try Again
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Logo */}
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <img
          src={`/${import.meta.env.VITE_LOGO_PATH}`}
          alt="Logo"
          style={{ maxWidth: '400px', width: '100%', height: 'auto' }}
        />
      </Box>
      
      {/* Save Status Indicator - Only show loading indicator */}
      {saveStatus.saving && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      
      {/* Current Step Indicator for Mobile */}
      {isMobile && (
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h6" color="primary">
            {steps[currentStep]}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Step {currentStep + 1} of {steps.length}
          </Typography>
        </Box>
      )}
      
      {/* Stepper - Hidden on Mobile */}
      {!isMobile && (
        <Stepper activeStep={currentStep} alternativeLabel>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepButton onClick={() => handleStepClick(index)}>
                {label}
              </StepButton>
            </Step>
          ))}
        </Stepper>
      )}
      
      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 4 }}>
        {renderStepContent(currentStep)}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item>
            {currentStep !== 0 && (
              <Button variant="contained" onClick={handleBack}>
                Back
              </Button>
            )}
          </Grid>
          <Grid item xs>
            {currentStep < steps.length - 1 && (
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleNext}
                disabled={saveStatus.saving}
              >
                {saveStatus.saving ? <CircularProgress size={24} /> : "Next"}
              </Button>
            )}
            {currentStep === steps.length - 1 && (
              <Button 
                variant="contained" 
                color="success" 
                type="submit"
                disabled={saveStatus.saving}
              >
                {saveStatus.saving ? <CircularProgress size={24} /> : "Submit"}
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default FormApplication;